import { Data, Operation, Variables } from '__generated__/graphql';
import { fetchWithProblem } from 'utils/Problem';
import { graphql } from '$/modules/graphql-client/graphql';

export type ClientOperationResponse<T> = {
  data: T | undefined;
  errors: Array<{ message?: string }>;
};

/**
 * Fetch a GraphQL query for use on the client
 **/
export async function* fetchClientOperation<T extends Operation>(
  operation: T,
  variables?: Variables<T>,
  options?: { signal?: AbortSignal; acceptedLanguage?: string },
): AsyncGenerator<
  ClientOperationResponse<Data<T>>,
  ClientOperationResponse<Data<T>>
> {
  if (typeof window === 'undefined') {
    throw new Error(
      `When fetching from the server, please use the "fetch${operation.name}" method instead`,
    );
  }

  const response = await fetchWithProblem('/api/gql', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'accept-language': options?.acceptedLanguage ?? '*',
    },
    body: JSON.stringify({
      id: operation.id,
      variables: variables,
    }),
    signal: options?.signal,
  });

  let part: ClientOperationResponse<Data<T>> = { data: undefined, errors: [] };
  for await (const chunk of graphql(response)) {
    part = { data: chunk.data as Data<T>, errors: chunk.errors };
    yield part;
  }
  // Return in addition to yielding to get better TS types
  return part;
}
